import * as babel from "@babel/standalone"

// Initialize Babel with necessary plugins
const initBabel = () => {
  // Check if we've already initialized
  if ((window as any).__babelPluginsInitialized) return

  try {
    // Register the plugins we'll use if they're available in the standalone version
    if (babel.availablePlugins) {
      // These should be available in @babel/standalone
      babel.registerPlugin(
        "@babel/plugin-transform-destructuring",
        babel.availablePlugins["transform-destructuring"],
      )
      babel.registerPlugin(
        "@babel/plugin-transform-spread",
        babel.availablePlugins["transform-spread"],
      )
      babel.registerPlugin(
        "@babel/plugin-transform-runtime",
        babel.availablePlugins["transform-runtime"],
      )
    }

    // Mark as initialized
    ;(window as any).__babelPluginsInitialized = true
  } catch (e) {
    console.error("Failed to initialize Babel plugins:", e)
  }
}

interface TranspileResult {
  success: boolean
  transpiledCode: string
  error?: string
}

/**
 * Transpiles TypeScript code to JavaScript
 * @param code TypeScript code to transpile
 * @returns A promise that resolves to a TranspileResult
 */
export async function transpileTypeScript(
  code: string,
): Promise<TranspileResult> {
  if (!code.trim()) {
    return {
      success: true,
      transpiledCode: "",
    }
  }

  try {
    // Initialize Babel plugins before transpiling
    initBabel()

    // Configure Babel to transpile TypeScript and JSX with better browser compatibility
    const result = babel.transform(code, {
      presets: [
        ["typescript", { allowNamespaces: true }],
        ["react", { pragma: "React.createElement" }],
        [
          "env",
          {
            targets: { ie: "11" },
            modules: "commonjs", // Explicitly transform to CommonJS format
          },
        ],
      ],
      // Using a simpler configuration that's more likely to work with @babel/standalone
      plugins: [["transform-destructuring", { loose: true }]],
      filename: "main.tsx",
      retainLines: true,
    })

    // Get the transpiled code
    let transformedCode = result?.code || ""

    // Determine the component name from the code
    // Look for either "export default function Name" or "function Name"
    const componentNameMatch = code.match(
      /export\s+default\s+function\s+(\w+)|function\s+(\w+)/,
    )
    const componentName = componentNameMatch
      ? componentNameMatch[1] || componentNameMatch[2]
      : "PluginComponent"

    // Add compatibility helpers at the top
    const compatibilityHelpers = `
// Browser compatibility helpers
var _slicedToArray = function (arr, i) {
  return (
    Array.isArray(arr) ? arr :                         // _arrayWithHoles(arr)
    Array.prototype.slice.call(arr, 0, i) || []        // Simple replacement for Symbol.iterator
  );
};

// Make sure window exists
var globalThis = typeof window !== 'undefined' ? window : global;
`

    // Replace the ES module export with a direct global assignment plus CommonJS export
    transformedCode = transformedCode.replace(
      /export\s+default\s+(\w+);?/g,
      `
// Expose component globally for direct access
globalThis["${componentName}"] = $1;
// Also support CommonJS
if (typeof module !== 'undefined') module.exports = $1;
`,
    )

    // Append a final line that explicitly assigns the component to a known global variable
    transformedCode += `
// Final assignment to ensure component is available
globalThis["CurrentPluginComponent"] = typeof ${componentName} !== 'undefined' ? ${componentName} : module.exports;
`

    // Return the transpiled code with the helpers prepended
    return {
      success: true,
      transpiledCode: compatibilityHelpers + transformedCode,
    }
  } catch (error) {
    console.error("Transpilation error:", error)
    return {
      success: false,
      transpiledCode: "",
      error: error instanceof Error ? error.message : "Unknown error",
    }
  }
}

// A very basic fallback transpiler for when TypeScript isn't available
// This only handles the most common cases and should be replaced with a proper solution
function simpleFallbackTranspiler(code: string): string {
  // Remove type annotations
  const result = code
    // Remove interface declarations
    .replace(/interface\s+[a-zA-Z0-9_]+\s*\{[\s\S]*?\}/g, "")
    // Remove type imports
    .replace(/import\s+type\s+.*?from\s+['"].*?['"]/g, "")
    // Remove type declarations in function parameters and variables
    .replace(/:\s*[a-zA-Z0-9_<>[\].,|&()\s]+(?=[,)=;])/g, "")
    // Remove return type declarations
    .replace(/\)\s*:\s*[a-zA-Z0-9_<>[\].,|&()\s]+\s*(?=[{=])/g, ")")
    // Remove type assertions
    .replace(/as\s+[a-zA-Z0-9_<>[\].,|&()\s]+/g, "")
    // Remove generic type parameters
    .replace(/<[a-zA-Z0-9_<>[\].,|&()\s]+>/g, "")

  return result
}
